import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, Outlet, useLocation } from 'react-router'
import { Dialog } from 'radix-ui'
import { Badge } from '@/components/ui/Badge'
import { Icon } from '@/components/ui/Icon'
import { partnerAppearance } from '@/components/ui/status'
import { navItems } from '@/components/layout/nav'
import { usePartnerProfile } from '@/features/auth/api'
import { useAuth } from '@/features/auth/auth-context'
import { TermsEntryPrompt } from '@/features/terms/TermsEntryPrompt'
import { cn } from '@/lib/cn'
import { useMocks } from '@/lib/env'

type NavListProps = { pending: boolean; onNavigate?: () => void }

function NavList({ pending, onNavigate }: NavListProps) {
  const { t } = useTranslation()

  return (
    <nav aria-label={t('nav.label')} className="flex flex-col gap-1 p-3">
      {navItems.map((item) => {
        const locked = pending && !item.openWhilePending

        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            aria-disabled={locked || undefined}
            title={locked ? t('partner.pendingTitle') : undefined}
            onClick={(event) => {
              if (locked) {
                event.preventDefault()
                return
              }

              onNavigate?.()
            }}
            className={({ isActive }) =>
              cn(
                'flex h-control-sm items-center gap-3 rounded-md px-2 text-sm font-medium',
                'transition-colors duration-250 ease-brand',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                locked
                  ? 'cursor-not-allowed text-ink-disabled'
                  : isActive
                    ? 'nav-accent relative bg-surface/70 text-accent-text shadow-xs'
                    : 'text-ink-secondary hover:bg-surface/70 hover:text-ink',
              )
            }
          >
            <Icon name={item.icon} className="size-5" />
            {t(item.labelKey)}
            {locked ? <Icon name="clock" className="ml-auto size-4" /> : null}
          </NavLink>
        )
      })}
    </nav>
  )
}

export function AppShell() {
  const { t } = useTranslation()
  const [menuOpen, setMenuOpen] = useState(false)
  const { signOut } = useAuth()
  const profile = usePartnerProfile()
  const status = profile.data?.status
  const pending = status === 'PENDING_APPROVAL'
  const location = useLocation()

  return (
    <div className="stage min-h-dvh">
      <div aria-hidden="true" className="stage-glow -top-60 -right-40 h-200 w-160" />
      <div
        aria-hidden="true"
        className="stage-glow stage-glow--alt -bottom-80 left-20 h-200 w-180"
      />

      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-surface focus:px-3 focus:py-2 focus:text-sm focus:text-ink focus:outline-2 focus:outline-ring"
      >
        {t('app.skipToContent')}
      </a>

      <div className="mx-auto flex max-w-app">
        <aside className="glass glass-edge sticky top-0 hidden h-dvh w-sidebar shrink-0 overflow-y-auto border-r md:block">
          <div className="glass-edge flex h-control-lg items-center gap-2 border-b px-4">
            <img src="/brand/logo-mark.svg" alt={t('app.logoAlt')} className="size-6" />
            <span className="truncate font-display text-sm font-semibold text-ink">
              {t('app.name')}
            </span>
            <span aria-hidden="true" className="pulse-dot size-1.5 rounded-full bg-brand-bright" />
          </div>
          <NavList pending={pending} />
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="glass sticky top-0 z-30 flex h-control-lg items-center gap-2 px-3 sm:px-4 lg:px-8">
            <Dialog.Root open={menuOpen} onOpenChange={setMenuOpen}>
              <Dialog.Trigger
                aria-label={t('nav.open')}
                className="inline-flex size-control-sm items-center justify-center rounded-md text-ink-secondary hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring md:hidden"
              >
                <Icon name="menu" />
              </Dialog.Trigger>
              <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40 md:hidden" />
                <Dialog.Content className="fixed top-0 left-0 z-50 h-dvh w-sidebar max-w-[85vw] overflow-y-auto border-r border-border bg-surface-muted focus-visible:outline-none md:hidden">
                  <div className="flex h-control-lg items-center justify-between border-b border-border px-4">
                    <Dialog.Title className="text-sm font-semibold text-ink">
                      {t('app.name')}
                    </Dialog.Title>
                    <Dialog.Close
                      aria-label={t('nav.close')}
                      className="inline-flex size-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                    >
                      <Icon name="close" className="size-4" />
                    </Dialog.Close>
                  </div>
                  <NavList
                    pending={pending}
                    onNavigate={() => {
                      setMenuOpen(false)
                    }}
                  />
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>

            <span className="truncate text-sm font-medium text-ink">
              {profile.data?.companyName ?? (profile.isPending ? '' : t('partner.companyUnknown'))}
            </span>

            {status === undefined ? null : (
              <Badge {...partnerAppearance[status]} className="max-sm:hidden">
                {t(`status.partner.${status}`)}
              </Badge>
            )}

            <div className="ml-auto flex items-center gap-2">
              {useMocks ? (
                <Badge tone="info" icon="info" className="max-lg:hidden">
                  {t('mocks.label')}
                </Badge>
              ) : null}
              <NavLink
                to="/security"
                aria-label={t('auth.setup.title')}
                title={t('auth.setup.title')}
                className={({ isActive }) =>
                  cn(
                    'inline-flex size-8 items-center justify-center rounded-md',
                    'transition-colors duration-250 ease-brand',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                    isActive
                      ? 'bg-accent-surface text-accent-text'
                      : 'text-ink-muted hover:bg-surface-hover hover:text-ink',
                  )
                }
              >
                <Icon name="shieldCheck" className="size-4" />
              </NavLink>
              <button
                type="button"
                aria-label={t('session.signOut')}
                title={t('session.signOut')}
                onClick={() => void signOut()}
                className="inline-flex size-8 items-center justify-center rounded-md text-ink-muted transition-colors duration-250 ease-brand hover:bg-surface-hover hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                <Icon name="logOut" className="size-4" />
              </button>
            </div>
            <div aria-hidden="true" className="hairline absolute inset-x-0 bottom-0" />
          </header>

          <main id="main" className="flex-1 p-4 sm:p-6 lg:p-8">
            {pending ? (
              <div className="mb-4 flex items-start gap-3 rounded-lg border border-warning/40 bg-warning-surface p-4">
                <Icon name="clock" className="mt-0.5 size-5 text-warning-strong" />
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-semibold text-ink">{t('partner.pendingTitle')}</p>
                  <p className="text-sm text-ink-muted">{t('partner.pendingDescription')}</p>
                </div>
              </div>
            ) : null}

            <div key={location.pathname} className="rise-in">
              <Outlet />
            </div>
          </main>
          <TermsEntryPrompt />
        </div>
      </div>
    </div>
  )
}
